import React, { useEffect } from 'react';
import {
  Button,
  Col,
  FloatingLabel,
  Form,
  FormGroup,
  FormLabel,
  Row,
} from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { ButtonSplitGroup } from '@components/Buttons';
import {
  FileUploadField,
  FileUploadFileTooBig,
} from '@components/Forms/CustomFields';
import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { HTTP_503_SERVICE_UNAVAILABLE } from '@config/const';
import { Page500 } from '@containers/ErrorPages';
import { ticketApi } from '@reducers/tickets/tickets';

const getInitialCountry = (country, countriesData) => {
  if (!country) {
    return '';
  }
  if (!countriesData || countriesData.length === 0) {
    return '';
  }
  const data = countriesData
    .filter(({ name }) => name.toLowerCase() === country.toLowerCase())
    .shift();
  if (!data) {
    return '';
  }
  return data.value;
};

const CreateNewTicketForm = () => {
  const { data, isError, error, isLoading } =
    ticketApi.useGetTicketCountriesQuery();
  const ticketTypeQuery = ticketApi.useGetTicketTypesQuery();
  const [createQuery, createResult] = ticketApi.useCreateTicketMutation();
  const location = useLocation();
  const schema = Yup.object({
    subject: Yup.string().required(),
    ticket_type: Yup.string().required(),
    description: Yup.string().required(),
    attachments: FileUploadFileTooBig(),
  });
  const did = location?.state?.did;
  const country = location?.state?.country;

  const navigate = useNavigate();
  useEffect(() => {
    if (
      createResult.isError ||
      createResult.isLoading ||
      createResult.isUninitialized
    ) {
      return;
    }
    navigate('/tickets/existing', {
      state: { created: !createResult.isError, data: createResult.data },
    });
  }, [createResult]);
  if (isLoading) {
    return <DefaultSpinnerCenter />;
  }
  if (isError) {
    if (error.status === HTTP_503_SERVICE_UNAVAILABLE) {
      return (
        <Page500 title="Service unavailable" getMessage={() => error.data} />
      );
    }
    return <Page500 />;
  }
  const initialValues = {
    subject: did ? `Ticket - DID ${did}` : '',
    dids: did || '',
    attachments: [],
    country: getInitialCountry(country, data),
    description: '',
    ticket_type: '',
  };

  const onSubmit = async (values) => {
    createQuery({ data: values });
  };
  const handleCancelButton = () => {
    navigate(-1);
  };
  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
      }) => (
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={12}>
              <FloatingLabel
                controlId="form.ControlSubject"
                label="Subject"
                className="mb-1"
              >
                <Form.Control
                  type="text"
                  placeholder="Subject"
                  name="subject"
                  value={values.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.subject && !errors.subject}
                  isInvalid={touched.subject && errors.subject}
                />
              </FloatingLabel>
              <FloatingLabel
                controlId="form.ControlDids"
                label="DIDs"
                className="mb-1"
              >
                <Form.Control
                  type="text"
                  placeholder="DIDs"
                  name="dids"
                  value={values.dids}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.dids && !errors.dids}
                  isInvalid={touched.dids && errors.dids}
                />
              </FloatingLabel>
              <FloatingLabel
                controlId="form.ControlCountry"
                label="Country"
                className="mb-1"
              >
                <Form.Select
                  className="mb-3"
                  name="country"
                  value={values.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isLoading}
                  aria-labelledby="form.ControlCountry"
                  isValid={touched.country && !errors.country}
                  isInvalid={touched.country && errors.country}
                >
                  <option value="">Select country</option>
                  {isLoading ? <option>Loading...</option> : null}
                  {!isError && !isLoading
                    ? data.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.name}
                        </option>
                      ))
                    : null}
                </Form.Select>
              </FloatingLabel>
              <FloatingLabel
                controlId="form.ControlTicketType"
                label="Ticket Type"
                className="mb-1"
              >
                <Form.Select
                  className="mb-3"
                  name="ticket_type"
                  value={values.ticket_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={ticketTypeQuery.isLoading}
                  aria-labelledby="form.ControlTicketType"
                  isValid={touched.ticket_type && !errors.ticket_type}
                  isInvalid={touched.ticket_type && errors.ticket_type}
                >
                  <option value="">Select ticket type</option>
                  {ticketTypeQuery.isLoading ? (
                    <option>Loading...</option>
                  ) : null}
                  {!ticketTypeQuery.isError && !ticketTypeQuery.isLoading
                    ? ticketTypeQuery.data.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.name}
                        </option>
                      ))
                    : null}
                </Form.Select>
              </FloatingLabel>
              <FormGroup className="mb-3" controlid="form.ControlAttachments">
                <FormLabel>Attachments:</FormLabel>
                <FileUploadField name="attachments" multiple />
              </FormGroup>
              <Form.Group controlId="form.ControlDescription">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Description"
                  name="description"
                  as="textarea"
                  rows={3}
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.description && !errors.description}
                  isInvalid={touched.description && errors.description}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col className="text-start mt-3" md={12}>
              <ButtonSplitGroup>
                <Button
                  variant="didxl"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={createResult.isLoading}
                >
                  Create{' '}
                  {createResult.isLoading ? <DefaultSpinnerCenter /> : null}
                </Button>
                <Button
                  variant="didxl-danger"
                  size="sm"
                  onClick={handleCancelButton}
                >
                  Cancel
                </Button>
              </ButtonSplitGroup>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};
export default CreateNewTicketForm;
